import { del } from '@nbx/frontend-helpers/fetch-api';
import { context } from '@nbx/frontend-helpers/auth';
import dayjs from 'dayjs-ext';
import isBetween from 'dayjs-ext/plugin/isBetween';
import Big from 'big.js';
import { getChartMinTickInterval, getIntervalInMinutes } from 'helpers/dates';
import { isNullish } from 'helpers/objects';
import setupDataFetcher from 'helpers/setupDataFetcher';

dayjs.extend(isBetween);
Big.RM = 0;

let baseUrl = 'markets';

const sortByCreatedAtAsc = (a, b) => {
  const dateA = dayjs(a.createdAt);
  const dateB = dayjs(b.createdAt);
  if (dateA > dateB) return 1;
  if (dateB > dateA) return -1;
  return 0;
};

export function processCandleSticks(trades, freq, start, end) {
  // If no trades, return empty array
  if (trades.length === 0) {
    return [];
  }

  // get interval in minutes
  const interval = getIntervalInMinutes(freq);

  // format start and end time to dayjs instances
  const formattedStart = start instanceof dayjs ? start : dayjs(start);
  const formattedEnd = end instanceof dayjs ? end : dayjs(end);

  // filter out dates out of date range
  const tradesInDateRange = trades.filter(trade => {
    return dayjs(trade.createdAt).isAfter(formattedStart) || dayjs(trade.createdAt).isSame(formattedStart);
  });

  // if no trades in date range return the last trade time, volume(not from trade, will just be 0), open, high, low, close
  if (tradesInDateRange.length === 0) {
    const [lastTrade] = trades;
    const time = dayjs(lastTrade.createdAt).unix();
    const volume = 0;
    const open = parseFloat(lastTrade.price);
    const high = parseFloat(lastTrade.price);
    const low = parseFloat(lastTrade.price);
    const close = parseFloat(lastTrade.price);
    return [[time, volume, open, high, low, close]];
  }

  // Add the last trade before the date range, so we can get the proper opening price
  if (trades.length > tradesInDateRange.length) {
    tradesInDateRange.push(trades[tradesInDateRange.length]);
  }

  // get all the possible times for candlesticks at the current interval
  const intervalDates = [formattedStart];
  while (intervalDates[intervalDates.length - 1].isBefore(formattedEnd)) {
    const newIntervalDate = intervalDates[intervalDates.length - 1].add(interval, 'm');
    intervalDates.push(newIntervalDate);
  }

  // get all trades that fall into the candlestick buckets
  const candlesticks = [];
  intervalDates.forEach((intervalDate, i) => {
    if (intervalDates[i + 1]) {
      // get trades for each interval
      const intervalTrades = tradesInDateRange.filter(trade =>
        dayjs(trade.createdAt).isBetween(intervalDate, intervalDates[i + 1], null, '[)')
      );
      if (i === 0) {
        intervalTrades.unshift(tradesInDateRange[tradesInDateRange.length - 1]);
      }
      if (intervalTrades.length > 0) {
        // reduce values of trades for time, volume, open, high, low, close
        intervalTrades.sort(sortByCreatedAtAsc);
        const time = intervalDate.unix();
        // ignore first item for volume when dealing with first interval (is the price from before the requested time)
        const volumeTrades = i === 0 ? intervalTrades.slice(1, intervalTrades.length) : intervalTrades;
        let volume;
        try {
          volume = parseFloat(volumeTrades.reduce((total, trade) => total.plus(trade.quantity), Big(0)));
        } catch (err) {
          console.warn(`Could not calculate market volume:`, err);
          volume = null;
        }
        const open = parseFloat(intervalTrades[0].price);
        const high = Math.max(...intervalTrades.map(trade => trade.price));
        const low = Math.min(...intervalTrades.map(trade => trade.price));
        const close = parseFloat(intervalTrades[intervalTrades.length - 1].price);
        candlesticks.push([time, volume, open, high, low, close]);
      }
    }
  });

  return candlesticks;
}

const addTrade = (trade, tradeUnixMs, bar) =>
  bar
    ? {
        volume: bar.volume.plus(trade.quantity),
        open: bar.openUnixMs < tradeUnixMs ? bar.open : trade.price,
        high: bar.high === undefined ? trade.price : Math.max(bar.high, trade.price),
        low: bar.low === undefined ? trade.price : Math.min(bar.low, trade.price),
        close: bar.closeUnixMs > tradeUnixMs ? bar.close : trade.price,
        openUnixMs: bar.openUnixMs < tradeUnixMs ? bar.openUnixMs : tradeUnixMs,
        closeUnixMs: bar.closeUnixMs > tradeUnixMs ? bar.closeUnixMs : tradeUnixMs
      }
    : {
        volume: Big(trade.quantity),
        open: trade.price,
        high: trade.price,
        low: trade.price,
        close: trade.price,
        openUnixMs: tradeUnixMs,
        closeUnixMs: tradeUnixMs
      };

export function processCandleSticksNxt(trades, freq, start, end) {
  // If no trades, return empty array
  if (trades.length === 0) {
    return [];
  }

  // get interval in minutes
  const intervalInMinutes = getIntervalInMinutes(freq);

  // format start and end time to dayjs instances
  const dayjsStart = start instanceof dayjs ? start : dayjs(start);
  const dayjsEnd = end instanceof dayjs ? end : dayjs(end);

  // // filter out dates out of date range
  const tradesInDateRange = trades.filter(trade => {
    return dayjs(trade.createdAt).isAfter(dayjsStart) || dayjs(trade.createdAt).isSame(dayjsStart);
  });

  const startUnixMs = dayjsStart.valueOf();
  const endUnixMs = dayjsEnd.valueOf();

  const getBarIdx = tradeUnixMs => Math.floor((tradeUnixMs - startUnixMs) / (intervalInMinutes * 60 * 1000));

  const getBarIntervalTime = bucket =>
    Math.floor((startUnixMs + bucket * (intervalInMinutes * 60 * 1000)) / 1000);

  const bars = [];

  bars[getBarIdx(startUnixMs)] = {
    volume: Big(0)
  };

  bars[getBarIdx(endUnixMs)] = {
    volume: Big(0)
  };

  tradesInDateRange.forEach(trade => {
    const tradeUnixMs = dayjs(trade.createdAt).valueOf();
    if (tradeUnixMs < startUnixMs || tradeUnixMs > endUnixMs) return;
    const i = getBarIdx(tradeUnixMs);
    bars[i] = addTrade(trade, tradeUnixMs, bars[i]);
  });

  let barWithLastTradeIdx = 0;

  const allBarsIntervals = Array.from(bars, (bar, i) => {
    if (bar) {
      barWithLastTradeIdx = i;
      return [
        getBarIntervalTime(i),
        parseFloat(bar.volume),
        parseFloat(bar.open),
        parseFloat(bar.high),
        parseFloat(bar.low),
        parseFloat(bar.close)
      ];
    }
    return [
      getBarIntervalTime(i),
      0,
      parseFloat(bars[barWithLastTradeIdx]?.close),
      parseFloat(bars[barWithLastTradeIdx]?.close),
      parseFloat(bars[barWithLastTradeIdx]?.close),
      parseFloat(bars[barWithLastTradeIdx]?.close)
    ];
  });

  return allBarsIntervals;
}

async function fetchCandleSticks(pair, freq, start, end, pageCallback, cancelRef) {
  let filledDateRange = false;
  let nextPageUrl = null;
  let currentEnd = end;
  let trades = [];
  let processedCandleSticks = [];
  let callWasCached = false;
  const CALL_CACHE_MS = 100; // from my tests, usually it takes about 10-20ms to retrieve data from cache, so 100ms is safe threshold.

  const renderCandles = () => {
    const partialCandles = processCandleSticks(trades, freq, start, currentEnd);
    const filledPartialCandles = fillSticks(freq, start, currentEnd, partialCandles);
    pageCallback(filledPartialCandles);
  };

  try {
    /* eslint-disable no-await-in-loop, prefer-destructuring */
    while (!filledDateRange && cancelRef?.current !== true) {
      callWasCached = false;
      const callStartTimeMs = performance.now();
      const response = await NBXMarkets.getMarketTrades(pair, nextPageUrl);
      const callEndTimeMs = performance.now();
      const callTookTimeMs = callEndTimeMs - callStartTimeMs;

      if (callTookTimeMs <= CALL_CACHE_MS) {
        callWasCached = true;
      }
      nextPageUrl = response.nextPageUrl;
      trades = [...trades, ...response.body];

      if (pageCallback && !callWasCached) {
        // if call was not cached, we just render it right away, because we have no reason to believe that upcoming calls will be cached.
        renderCandles();
      }

      if (!nextPageUrl || dayjs(response.body[response.body.length - 1]?.createdAt).isBefore(dayjs(start))) {
        // Break the loop if the page is empty
        // If last trade is before is the start of the date range we're trying to fill
        // Or there is more data for this market
        // Then we can process the data we have and then break the loop
        processedCandleSticks = processCandleSticksNxt(trades, freq, start, end);
        filledDateRange = true;
      } else if (!dayjs(response.body[response.body.length - 1]?.createdAt).isAfter(dayjs(currentEnd))) {
        // Else we process the data we have, and fetch another page
        currentEnd = dayjs(trades[0].createdAt);
      }
    }
    /* eslint-enable no-await-in-loop */
  } catch (error) {
    console.error('error fetching trades', error); // eslint-disable-line
  }

  if (processedCandleSticks.length > 0 && callWasCached && pageCallback) {
    // Render the candlesticks that haven't been rendered yet, because we have detected to receive them from cache.
    renderCandles();
  }
  return [processedCandleSticks, trades];
}

function fillSticks(freq, start, end, candlesticksArg) {
  const candlesticks = [...candlesticksArg];
  const endAsMs = dayjs(end).valueOf();
  const startAsMs = dayjs(start).valueOf();
  const tick = getChartMinTickInterval(freq);
  const filledSticks = [];
  let lastPrice;

  // If the most recent trade is before the time period we want, fill time period with its price
  if (candlesticks.length === 1) {
    const date = candlesticks[0][0] * 1000;
    if (date < startAsMs) {
      lastPrice = candlesticks[0][5]; // eslint-disable-line
      candlesticks.pop();
      fillToPoint(startAsMs, endAsMs, tick, parseFloat(lastPrice), filledSticks);
    }
    // If there is no data, just fill the period with market price, if it exists
  } else if (candlesticks.length === 0 && !isNullish(lastPrice)) {
    fillToPoint(startAsMs, endAsMs, tick, parseFloat(lastPrice), filledSticks);
  }

  candlesticks.forEach((item, index) => {
    const date = item[0] * 1000;
    // If the first candlestick isn't at the beginning of the range
    // extend its opening price back to then
    if (index === 0) {
      if (date - startAsMs > tick) {
        fillToPoint(startAsMs, date, tick, item[2], filledSticks);
      }
    }

    // If not the first candlestick, make sure opening/closing price of adjacent candlesticks line-up
    if (index > 0) {
      item = adjustOpen(item, filledSticks, 1);
    }

    filledSticks.push(item);

    if (index < candlesticks.length - 1) {
      const nextDate = candlesticks[index + 1][0] * 1000;
      // If the space between this point and the next is more than a chart tick
      // add filler candlesticks with this candlestick's closing as their price
      if (date >= startAsMs && nextDate - date > tick) {
        fillToPoint(date + tick, nextDate, tick, item[5], filledSticks);
      }
    } else if (index === candlesticks.length - 1) {
      // If the last candlestick doesn't reach the end of the chart range
      // extend its closing price forward to now
      if (endAsMs - date > tick) {
        fillToPoint(Math.max(date + tick, startAsMs + tick), endAsMs, tick, item[5], filledSticks);
      }
    }
  });

  return filledSticks;
}

async function getAndRenderCandlesticksForCharts(pair, freq, start, end, pageCallback, cancelRef) {
  const [processedCandleSticks] = await fetchCandleSticks(pair, freq, start, end, pageCallback, cancelRef);
  const filledSticks = fillSticks(freq, start, end, processedCandleSticks);
  return filledSticks;
}

function adjustOpen(item, sticks, subtraction) {
  const prevPoint = sticks[sticks.length - subtraction];
  // prevPoint[5] is closing price, since it is in the og order
  // Make sure that opening of this stick and the closing of the last one line up
  if (prevPoint[5] < item[2]) {
    // If the price rose, adjust opening, check if we need to adjust low point for stick
    item[2] = prevPoint[5]; // eslint-disable-line
    if (item[4] > prevPoint[5]) item[4] = prevPoint[5]; // eslint-disable-line
  } else if (prevPoint[5] > item[2]) {
    // If the price fell, adjust opening, check if we need to adjust high point for stick
    item[2] = prevPoint[5]; // eslint-disable-line
    if (item[3] < prevPoint[5]) item[3] = prevPoint[5]; // eslint-disable-line
  }
  return item;
}

function fillToPoint(startAsMs, end, tick, price, sticksData) {
  let pointDate = startAsMs;
  while (pointDate < end) {
    sticksData.push([pointDate / 1000, 0, price, price, price, price]);
    pointDate += tick;
  }
}

const NBXMarkets = {
  init: config => ({ baseUrl } = config),

  getMarkets: () => {
    return new Promise((resolve, reject) => {
      let disposer;
      const onResult = message => {
        resolve(message);
        disposer();
      };
      const onError = message => {
        reject(message);
        disposer();
      };
      const generateFetchKey = () => 'markets';
      disposer = setupDataFetcher(onResult, onError, generateFetchKey);
    });
  },

  getAssets: () => {
    return new Promise((resolve, reject) => {
      let disposer;
      const onResult = message => {
        resolve(message);
        disposer();
      };
      const onError = message => {
        reject(message);
        disposer();
      };
      const generateFetchKey = () => 'assets';
      disposer = setupDataFetcher(onResult, onError, generateFetchKey);
    });
  },

  getMarketTrades: (marketIdArg, nextPageUrlArg) => {
    return new Promise((resolve, reject) => {
      let disposer;

      const onResult = message => {
        resolve(message);
        disposer();
      };
      const onError = message => {
        console.error(`Received error while fetching market trades.`);
        reject(message);
        disposer();
      };
      const generateFetchKey = ({ marketId, nextPageUrl } = {}) => {
        if (marketId && nextPageUrl) {
          const urlPagingState = nextPageUrl.split('=')[1];
          return `trades-${marketId}-${urlPagingState.split('.').slice(0, 2).join('.')}`;
        }
        if (marketId) {
          return `trades-${marketId}`;
        }
        return 'trades';
      };
      disposer = setupDataFetcher(onResult, onError, generateFetchKey, null, {
        marketId: marketIdArg,
        nextPageUrl: nextPageUrlArg
      });
    });
  },

  createMarketEventsWS: pair => {
    return new WebSocket(`wss://api.nbx.com/markets/${pair}/events`); // there is no dev web socket yet.
  },

  getMarketOrders: (marketIdArg, rawDataArg) => {
    return new Promise((resolve, reject) => {
      let disposer;

      const onResult = message => {
        resolve(message);
        disposer();
      };
      const onError = message => {
        reject(message);
        disposer();
      };
      const generateFetchKey = ({ marketId, rawData } = {}) => {
        if (marketId && rawData) {
          return `orderBook-rawData-${marketId}`;
        }
        if (marketId && !rawData) {
          return `orderBook-${marketId}`;
        }
        return 'orderBook';
      };
      disposer = setupDataFetcher(onResult, onError, generateFetchKey, null, {
        marketId: marketIdArg,
        rawData: rawDataArg
      });
    });
  },

  getMarketTickers: async pair => {
    const freq = 'D';
    const start = dayjs(new Date()).subtract(1, 'day').toISOString();
    const end = dayjs(new Date());
    const [candlesticks, trades] = await fetchCandleSticks(pair, freq, start, end);
    let values = null;
    let ticker = null;
    if (candlesticks.length > 0) [values] = candlesticks;
    if (trades.length > 0) [ticker] = trades;

    const getValue = index => {
      if (values) return parseFloat(values[index]);
      return null;
    };

    const stats24h = {
      price: ticker?.price ?? null,
      volume: getValue(1),
      open: getValue(2),
      high: getValue(3),
      low: getValue(4),
      close: getValue(5)
    };

    const changeDiff = stats24h.close - stats24h.open;
    stats24h.changeAmount = changeDiff || 0;
    stats24h.isChangeUp = changeDiff > 0;
    stats24h.change = `${(changeDiff ? (changeDiff / stats24h.open) * 100 : 0).toFixed(2)}%`;

    return stats24h;
  },

  getAndRenderCandlesticksForCharts,

  cancelOrder: (marketId, orderId) =>
    context.account().then(account => del(`${baseUrl}/${marketId}/orders/${orderId}`, {}, account))
};

export default NBXMarkets;
